/**
 * Performance Monitoring Types
 * Real-time latency tracking for Makanly POS Platform
 */

// =============================================================================
// Latency Thresholds (milliseconds)
// =============================================================================

export const LATENCY_THRESHOLDS = {
  GREEN: 100, // < 100ms = Excellent
  YELLOW: 500, // 100-500ms = Warning
  // > 500ms = Critical (Red)
} as const;

export const ALERT_THRESHOLDS = {
  YELLOW_CONSECUTIVE: 3, // 3 consecutive yellows = warning alert
  RED_CONSECUTIVE: 2, // 2 consecutive reds = critical alert
} as const;

export const PROBE_CONFIG = {
  INTERVAL_MS: 10_000, // 10 seconds between probes
  HISTORY_SIZE: 50, // Last 50 readings for sparkline
  ROLLING_WINDOWS: {
    SHORT: 30_000, // 30 second rolling average
    LONG: 60_000, // 1 minute rolling average
  },
} as const;

// =============================================================================
// Status Types
// =============================================================================

export type LatencyStatus = 'green' | 'yellow' | 'red' | 'unknown';

export type AlertLevel = 'none' | 'warning' | 'critical';

// =============================================================================
// Sample & Metric Types
// =============================================================================

export interface LatencySample {
  timestamp: number; // Unix timestamp (ms)
  latencyMs: number; // Measured latency
  status: LatencyStatus; // Derived status based on thresholds
}

export interface ServiceProbeResult {
  service: string;
  url: string;
  latencyMs: number;
  success: boolean;
  error?: string;
  database?: {
    latencyMs: number;
    success: boolean;
    error?: string;
  };
  redis?: {
    latencyMs: number;
    success: boolean;
    error?: string;
  };
  serviceProcessingMs?: number;
}

export interface GatewayProbeResult {
  timestamp: string;
  gatewayProcessingMs: number;
  services: {
    auth: ServiceProbeResult;
    catalog: ServiceProbeResult;
    orders: ServiceProbeResult;
  };
  redis?: {
    latencyMs: number;
    success: boolean;
    error?: string;
  };
  nats?: {
    latencyMs: number;
    success: boolean;
    connected: boolean;
    server?: string;
    error?: string;
  };
  breakdown: {
    gateway: number;
    auth_db: number | null;
    catalog_db: number | null;
    orders_db: number | null;
    redis: number | null;
    nats: number | null;
    total: number;
  };
}

export interface WebSocketLatency {
  roundTripMs: number;
  serverProcessingMs: number;
  timestamp: number;
}

// =============================================================================
// Aggregate Metric Types
// =============================================================================

export interface PerfMetric {
  name: string;
  displayName: string;
  current: number | null;
  status: LatencyStatus;
  history: LatencySample[];
  rollingAvg30s: number | null;
  rollingAvg1min: number | null;
  consecutiveYellows: number;
  consecutiveReds: number;
  alertLevel: AlertLevel;
}

export interface PerfMetrics {
  // WebSocket latency
  wsRoundTrip: PerfMetric;

  // API Gateway
  gateway: PerfMetric;

  // Service Database latencies
  authDb: PerfMetric;
  catalogDb: PerfMetric;
  ordersDb: PerfMetric;

  // Redis latency (from Orders service)
  redis: PerfMetric;

  // NATS JetStream latency (from Orders service)
  nats: PerfMetric;

  // Overall system
  totalLatency: PerfMetric;
}

export interface PerfAlert {
  id: string;
  timestamp: number;
  level: AlertLevel;
  metric: string;
  displayName: string;
  message: string;
  currentMs: number;
  consecutiveCount: number;
  acknowledged: boolean;
}

// =============================================================================
// Hook State Types
// =============================================================================

export interface PerfMonitorState {
  metrics: PerfMetrics;
  isConnected: boolean;
  isProbing: boolean;
  lastProbeTime: number | null;
  lastError: string | null;
  alerts: PerfAlert[];
  connectionAttempts: number;
}

export interface UsePerfMonitorOptions {
  autoStart?: boolean;
  onAlert?: (alert: PerfAlert) => void;
  enableBrowserNotifications?: boolean;
}

export interface UsePerfMonitorReturn {
  state: PerfMonitorState;
  startMonitoring: () => void;
  stopMonitoring: () => void;
  probeNow: () => Promise<void>;
  acknowledgeAlert: (alertId: string) => void;
  clearAlerts: () => void;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Determine latency status based on thresholds
 */
export function getLatencyStatus(latencyMs: number | null): LatencyStatus {
  if (latencyMs === null) return 'unknown';
  if (latencyMs < LATENCY_THRESHOLDS.GREEN) return 'green';
  if (latencyMs < LATENCY_THRESHOLDS.YELLOW) return 'yellow';
  return 'red';
}

/**
 * Get color class for latency status
 */
export function getLatencyColor(status: LatencyStatus): string {
  switch (status) {
    case 'green':
      return 'text-green-500';
    case 'yellow':
      return 'text-yellow-500';
    case 'red':
      return 'text-red-500';
    default:
      return 'text-gray-400';
  }
}

/**
 * Get background color class for latency status
 */
export function getLatencyBgColor(status: LatencyStatus): string {
  switch (status) {
    case 'green':
      return 'bg-green-500';
    case 'yellow':
      return 'bg-yellow-500';
    case 'red':
      return 'bg-red-500';
    default:
      return 'bg-gray-400';
  }
}

/**
 * Get badge variant for latency status
 */
export function getLatencyBadgeVariant(
  status: LatencyStatus,
): 'success' | 'warning' | 'destructive' | 'secondary' {
  switch (status) {
    case 'green':
      return 'success';
    case 'yellow':
      return 'warning';
    case 'red':
      return 'destructive';
    default:
      return 'secondary';
  }
}

/**
 * Format latency for display
 */
export function formatLatency(ms: number | null): string {
  if (ms === null) return '-';
  if (ms < 1) return '<1ms';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

/**
 * Calculate rolling average from samples
 */
export function calculateRollingAverage(
  samples: LatencySample[],
  windowMs: number,
  now: number = Date.now(),
): number | null {
  const cutoff = now - windowMs;
  const recentSamples = samples.filter((s) => s.timestamp >= cutoff);

  if (recentSamples.length === 0) return null;

  const sum = recentSamples.reduce((acc, s) => acc + s.latencyMs, 0);
  return sum / recentSamples.length;
}

/**
 * Determine alert level based on consecutive status counts
 */
export function determineAlertLevel(
  consecutiveYellows: number,
  consecutiveReds: number,
): AlertLevel {
  if (consecutiveReds >= ALERT_THRESHOLDS.RED_CONSECUTIVE) {
    return 'critical';
  }
  if (consecutiveYellows >= ALERT_THRESHOLDS.YELLOW_CONSECUTIVE) {
    return 'warning';
  }
  return 'none';
}

/**
 * Create initial empty metric
 */
export function createEmptyMetric(name: string, displayName: string): PerfMetric {
  return {
    name,
    displayName,
    current: null,
    status: 'unknown',
    history: [],
    rollingAvg30s: null,
    rollingAvg1min: null,
    consecutiveYellows: 0,
    consecutiveReds: 0,
    alertLevel: 'none',
  };
}

/**
 * Create initial empty metrics state
 */
export function createInitialMetrics(): PerfMetrics {
  return {
    wsRoundTrip: createEmptyMetric('wsRoundTrip', 'WebSocket RTT'),
    gateway: createEmptyMetric('gateway', 'API Gateway'),
    authDb: createEmptyMetric('authDb', 'Auth DB'),
    catalogDb: createEmptyMetric('catalogDb', 'Catalog DB'),
    ordersDb: createEmptyMetric('ordersDb', 'Orders DB'),
    redis: createEmptyMetric('redis', 'Redis'),
    nats: createEmptyMetric('nats', 'NATS JetStream'),
    totalLatency: createEmptyMetric('totalLatency', 'Total Latency'),
  };
}

/**
 * Update metric with new sample
 */
export function updateMetricWithSample(
  metric: PerfMetric,
  latencyMs: number,
  timestamp: number = Date.now(),
): PerfMetric {
  const status = getLatencyStatus(latencyMs);
  const sample: LatencySample = { timestamp, latencyMs, status };

  // Add sample to history, keeping only HISTORY_SIZE entries
  const history = [...metric.history, sample].slice(-PROBE_CONFIG.HISTORY_SIZE);

  // Calculate consecutive counts
  let consecutiveYellows = status === 'yellow' ? metric.consecutiveYellows + 1 : 0;
  let consecutiveReds = status === 'red' ? metric.consecutiveReds + 1 : 0;

  // Reset opposite counter when status changes
  if (status === 'green') {
    consecutiveYellows = 0;
    consecutiveReds = 0;
  }

  const rollingAvg30s = calculateRollingAverage(
    history,
    PROBE_CONFIG.ROLLING_WINDOWS.SHORT,
    timestamp,
  );
  const rollingAvg1min = calculateRollingAverage(
    history,
    PROBE_CONFIG.ROLLING_WINDOWS.LONG,
    timestamp,
  );

  const alertLevel = determineAlertLevel(consecutiveYellows, consecutiveReds);

  return {
    ...metric,
    current: latencyMs,
    status,
    history,
    rollingAvg30s,
    rollingAvg1min,
    consecutiveYellows,
    consecutiveReds,
    alertLevel,
  };
}

/**
 * Generate unique alert ID
 */
export function generateAlertId(): string {
  return `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}
